import { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { AICA_SESSION_PARAM } from "./ai-curriculum.constants";
import { AiCurriculumModal } from "./ai-curriculum.modal";

/**
 * Entry-point button + wizard for the AI Curriculum Assistant.
 *
 * Renders an "AI Curriculum Assistant" button that opens the full-screen
 * wizard modal on click. The caller is responsible for gating visibility
 * (org-level `ai-curriculum-enabled` flag, read-only state, etc.) — see
 * how the Course Builder tab wraps this with useAiCurriculumEnabled().
 *
 * If `hasExistingCourse` is true, the user must confirm replacing the existing
 * course before the wizard opens. (The backend will rebuild in place
 * regardless — the confirmation is a UX safeguard, not a security boundary.)
 *
 * `disabled` mirrors the parent's read-only/archived state. When true the
 * button still renders but won't accept clicks.
 *
 * `onCourseBuilt` is invoked with the build response when the wizard finishes
 * so the parent (the Course Builder tab) can refetch courseInfo and show the
 * newly built course.
 */
export const AiCurriculumLauncher = ({
  orgCode,
  classroomId,
  hasExistingCourse = false,
  disabled = false,
  onCourseBuilt,
  className,
  label = "AI Curriculum Assistant",
  hideIcon,
}) => {
  const location = useLocation();
  // Reopen after a refresh (ENG-23196). Mount-only: later param changes are
  // the modal writing its own state back and must not force it open again.
  const [open, setOpen] = useState(() =>
    new URLSearchParams(location.search).has(AICA_SESSION_PARAM)
  );
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  const handleLaunchClick = () => {
    if (hasExistingCourse) {
      setConfirmReplaceOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleConfirmReplace = () => {
    setConfirmReplaceOpen(false);
    setOpen(true);
  };

  const handleBuildComplete = (result) => {
    // Don't auto-dismiss — the modal stays on the COMPLETE step so the user
    // can read the result. They close it via the "Done" button, which routes
    // back through onClose.
    onCourseBuilt?.(result);
  };

  return (
    <>
      <Button
        className={className}
        icon={!hideIcon && ["fa-regular", "wand-magic-sparkles"]}
        primary
        disabled={disabled}
        onClick={handleLaunchClick}
      >
        {label}
      </Button>
      {createPortal(
        <>
          <Modal
            modalClassName="ai-curriculum-confirm-replace-modal"
            open={confirmReplaceOpen}
            onClose={() => setConfirmReplaceOpen(false)}
          >
            <ModalHeader
              icon={["fa-regular", "triangle-exclamation"]}
              title="Replace Existing Course?"
            />
            <ModalBody>
              This classroom already has a course. Building a new one with the
              AI Curriculum Assistant will replace it. This action cannot be
              undone.
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setConfirmReplaceOpen(false)}>
                Cancel
              </Button>
              <Button destructive onClick={handleConfirmReplace}>
                Replace Course
              </Button>
            </ModalFooter>
          </Modal>
          <AiCurriculumModal
            open={open}
            onClose={() => setOpen(false)}
            orgCode={orgCode}
            classroomId={classroomId}
            onBuildComplete={handleBuildComplete}
          />
        </>,
        document.body
      )}
    </>
  );
};
